import { zodResolver } from '@hookform/resolvers/zod';
import { TextField, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import type { CreatePlanWizardValues } from '@/packages/idp/pages/create-plan/types';
import { FeedbackOptions } from '@/packages/idp/pages/create-plan/types';
import { ContentBox } from '@/components/shared/content-box';
import { useNotification } from '@/context/notification';
import { useWizard } from '@/context/wizard';
import { useCreateUserFeedback } from '@/data/guidance/feedback';
import { useUpdateProfileBasedRecommendation } from '@/data/idp/profile-based-recommendations';
import { RecommendationPlanIds } from '@/data/idp/profile-based-recommendations/interfaces';
import { StepActions } from '@/packages/idp/pages/create-plan/components/step-actions';

const MAX_FEEDBACK_LENGTH = 10000;

const feedbackValidationSchema = z.object({
  content: z
    .string()
    .min(1, 'Preencha com um feedback válido')
    .max(MAX_FEEDBACK_LENGTH, `Máximo de ${MAX_FEEDBACK_LENGTH} caracteres`),
});

type FeedbackFormData = z.infer<typeof feedbackValidationSchema>;

export const NewFeedback = () => {
  const { actions, values } = useWizard<CreatePlanWizardValues>();
  const { mutate: createFeedback, isPending: isCreating } =
    useCreateUserFeedback();
  const { mutate: updateRecommendation } = useUpdateProfileBasedRecommendation(
    values?.id || ''
  );
  const { toast } = useNotification();

  const { control, handleSubmit, watch } = useForm<FeedbackFormData>({
    defaultValues: {
      content: values?.[RecommendationPlanIds.NewFeedback] || '',
    },
    resolver: zodResolver(feedbackValidationSchema),
  });

  const currentContent = watch('content');

  const onSubmit = (data: FeedbackFormData) => {
    createFeedback(data.content, {
      onSuccess: (response) => {
        actions.updateValues({
          [RecommendationPlanIds.NewFeedback]: data.content,
          [RecommendationPlanIds.ProcessingFeedback]: {
            feedbackId: response.id,
          },
        });
        updateRecommendation(
          { current_step: RecommendationPlanIds.ProcessingFeedback },
          {
            onSuccess: () => {
              actions.navigate(RecommendationPlanIds.ProcessingFeedback);
            },
          }
        );
      },
      onError: () => {
        toast({ content: 'Erro ao criar feedback', type: 'error' });
      },
    });
  };

  const handleSkip = () => {
    updateRecommendation(
      { current_step: RecommendationPlanIds.SkillSelection },
      {
        onSuccess: () => {
          actions.updateValues({
            [RecommendationPlanIds.Feedback]: {
              currentFeedback:
                values?.[RecommendationPlanIds.Feedback]?.currentFeedback ||
                null,
              option: FeedbackOptions.Skip,
            },
            [RecommendationPlanIds.NewFeedback]: null,
          });
          actions.navigate(RecommendationPlanIds.SkillSelection);
        },
      }
    );
  };

  const handleBack = () => {
    const hasPreviousFeedback =
      !!values?.[RecommendationPlanIds.Feedback]?.currentFeedback;
    if (hasPreviousFeedback) {
      actions.navigate(RecommendationPlanIds.Feedback);
    } else {
      actions.navigate(RecommendationPlanIds.Duration);
    }
  };

  return (
    <ContentBox>
      <Typography variant="h2">Feedback ou avaliação</Typography>
      <Typography mb={3} mt={1}>
        Seus feedbacks ajudam a IA a entender melhor suas forças e
        oportunidades. Usar esse histórico é a melhor forma de deixar seu plano
        ainda mais estratégico e conectado com o seu momento atual.
      </Typography>
      <Typography variant="body2" mb={2}>
        Copie e cole o seu último feedback no campo de texto abaixo para
        gerarmos habilidades baseadas no seu contexto.
      </Typography>
      <Controller
        control={control}
        name="content"
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            error={!!error}
            fullWidth
            helperText={
              error?.message ||
              `${currentContent?.length || 0}/${MAX_FEEDBACK_LENGTH} ${currentContent?.length === MAX_FEEDBACK_LENGTH ? 'Você atingiu o limite de caracteres.' : ''}`
            }
            multiline
            minRows={10}
            placeholder="Ex.: O próximo ciclo você precisará desenvolver sua comunicação, tempo de reatividade e visibilidade nos assuntos."
            slotProps={{ htmlInput: { maxLength: MAX_FEEDBACK_LENGTH } }}
        />
      )}
      />
      <StepActions
        onBack={handleBack}
        onSkip={handleSkip}
        onNext={handleSubmit(onSubmit)}
        nextDisabled={isCreating}
        nextLoading={isCreating}
      />
    </ContentBox>
  );
};
